/**
 * Custom fetch implementation used by the Orval-generated API client.
 *
 * Orval generates request functions that delegate the actual network call to this
 * mutator (configured via `output.override.mutator` in `orval.config.ts`). It is the
 * single integration point where authentication and response handling live for every
 * generated endpoint, so it intentionally re-implements the essential behavior of the
 * legacy `prepareRequest` / `prepareInternalRequest` / `doRequest` helpers in
 * `shared-frontend/functions/api.js` — minus the parts we want to leave behind:
 *
 *   - No bespoke query-string serializer: Orval builds query strings from the OpenAPI
 *     spec, so we pass `options` through untouched.
 *   - No `new Promise((resolve, reject) => ...)` wrapper: plain async/await.
 *   - No `"let-the-browser-set-this"` Content-Type sentinel: FormData bodies simply
 *     omit the JSON Content-Type so the browser sets the multipart boundary.
 *   - No lodash deep-merge ceremony for request options.
 *
 * What we deliberately keep, because the rest of the app relies on it:
 *   - Impersonation-aware Bearer token injection.
 *   - `401 invalidAccessToken` → clear auth cookies and redirect to login.
 *   - `429` → a friendly, user-facing error message.
 *   - `204` / empty body → resolve `undefined`.
 *   - Errors carry the failing `Response` so React Query (and callers) can branch on it.
 */
import { appPrefersImpersonator, getAccessToken, removeCookies } from "shared-frontend/functions/auth";
import getEnv from "shared-frontend/functions/getEnv";

/**
 * Error thrown for non-ok responses and network-level failures.
 *
 * The failing fetch {@link Response} is attached as `response`, giving callers (and React
 * Query) the standard Response API to branch on — `response.status`, `response.headers`,
 * `response.ok`, etc. The body stream is left unread: `customFetch` parses a *clone* for
 * its own status handling, so `error.response.json()` still works for the caller.
 *
 * `response` is `undefined` only for network-level failures (offline, DNS, CORS, abort),
 * where fetch never produced a Response at all — branch on its absence to detect
 * "couldn't reach the server".
 *
 * `body` is the parsed JSON of a non-ok response, so a render can read the server's reason
 * without awaiting the stream; `undefined` when there was no JSON body.
 */
export class ApiError extends Error {
	public readonly response?: Response;

	public readonly body?: unknown;

	public constructor( response?: Response, message?: string, body?: unknown ) {
		super( message ?? ( response
			? `Request failed with status ${ response.status }`
			: "Network request failed" ) );
		this.name = "ApiError";
		this.response = response;
		this.body = body;
	}
}

/**
 * The default error type for every generated hook's `TError` generic.
 *
 * Orval keys on a mutator export named exactly `ErrorType`: when present, it makes this
 * the default `TError` across all generated query/mutation hooks — so `someHook(...).error`
 * is typed `ApiError | null` instead of `void`, without annotating each call. Do NOT rename
 * or remove this — regenerating the client (`yarn api:generate`) depends on the name.
 *
 * The generated client always references it as `ErrorType<TSpecError>` (e.g. `ErrorType<void>`),
 * so the type parameter must stay for those references to compile. It is intentionally unused:
 * our errors expose the raw `Response`, not a spec-typed body, so the argument is ignored.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for generated `ErrorType<...>` references.
export type ErrorType<TSpecError = unknown> = ApiError;


/**
 * Builds the request headers, injecting the impersonation-aware Bearer token when
 * available. A JSON Content-Type is applied for non-FormData bodies only; FormData is
 * left untouched so the browser can set the multipart boundary.
 *
 * @param options The request options provided by the generated client.
 *
 * @returns The headers to send with the request.
 */
function buildHeaders( options: RequestInit ): Headers {
	const headers = new Headers( options.headers );

	const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
	if ( ! isFormData && ! headers.has( "Content-Type" ) && options.body !== undefined ) {
		headers.set( "Content-Type", "application/json" );
	}

	const token = getAccessToken( appPrefersImpersonator() );
	if ( token ) {
		headers.set( "Authorization", `Bearer ${ token }` );
	}

	return headers;
}

/**
 * Clears auth state and redirects to the login page. Used when the server reports the
 * access token is no longer valid, mirroring the legacy `handleInvalidAccessToken`.
 *
 * @returns Never returns; navigation is triggered as a side effect.
 */
function handleInvalidAccessToken(): void {
	removeCookies();
	const loginUrl = getEnv( "LOGIN_URL", "https://my.yoast.test/login" );
	document.location.href = `${ loginUrl }?redirect_to=${ encodeURIComponent( window.location.href ) }`;
}

/**
 * Parses the JSON body of a response, tolerating empty bodies.
 *
 * @param response The fetch response.
 *
 * @returns The parsed body, or `undefined` when there is no body.
 */
async function parseBody<T>( response: Response ): Promise<T | undefined> {
	try {
		return ( await response.json() ) as T;
	} catch {
		return undefined;
	}
}

/**
 * The Orval mutator. Executes a request and returns the parsed response body.
 *
 * @param url The URL to call.
 * @param options The request options provided by the generated client.
 *
 * @returns A promise resolving to the typed response body.
 */
export const customFetch = async <T>( url: string, options: RequestInit = {} ): Promise<T> => {
	let response: Response;
	try {
		response = await fetch( url, {
			...options,
			headers: buildHeaders( options ),
		} );
	} catch ( error ) {
		// fetch only rejects on network-level failures (offline, DNS, CORS, abort) — there
		// is no HTTP response. Normalize to an ApiError (no response) so every caller sees
		// one error type and can detect "couldn't reach the server" via the absent response.
		const message = error instanceof Error ? error.message : "Network request failed";
		throw new ApiError( undefined, message );
	}

	if ( response.status === 429 ) {
		// The friendly copy is the user-facing message; the full Response (incl. any
		// Retry-After header) rides along on the error for callers that want it.
		throw new ApiError( response, "We got too many requests in a short timespan. Please try again later." );
	}

	if ( ! response.ok ) {
		// Read a clone so the Response attached to the error keeps an unread body stream.
		const body = await parseBody<{ invalidAccessToken?: boolean }>( response.clone() );

		if ( response.status === 401 && body?.invalidAccessToken ) {
			handleInvalidAccessToken();
		}

		throw new ApiError( response, undefined, body );
	}

	return ( await parseBody<T>( response ) ) as T;
};

export default customFetch;
